<template>
  <div>
    <vx-card>
      <p class="mb-5">
        Information in this section (Introduction) does not need to be learned or memorised and you
        will not be quizzed on it.
      </p>
      <p>
        This information is provided for you to read through and gain an understanding of this
        particular career path before starting your Virtual Job Experience.
      </p>
    </vx-card>
    <vx-card class="mt-base mb-10">
      <h4 class="mb-5">Let's start your Environmental Science experience!</h4>
      <p class="mb-5">
        There is a growing consciousness around the world regarding the impact that we have on our
        environment. The field of environmental science looks at the relationship between people and
        the natural world around them. It looks to find ways to lessen the impact that human beings
        and their activities have on the environment, plant and animal life included.
      </p>

      <p class="mb-5">
        There are many job possibilities to consider for those who would like to pursue a career in
        this field. Some options include park ranger, environmental scientist, recycling officer,
        marine biologist and conservation officer. Many of these jobs share common ground in terms
        of their tasks and the skills they require. Below, we take a look at three of these:
      </p>
      <ul class="default-list">
        <li>Park Ranger</li>
        <li>Conservation Officer</li>
        <li>Environmental Scientist</li>
      </ul>
    </vx-card>

    <vs-tabs alignment="fixed" v-model="activeTab" class="mb-5 no-padding overwrite-tabs">
      <vs-tab label="Park Ranger">
        <div class="py-2">
          <vx-card class="no-shadow">
            <h4 class="mb-5">What they do:</h4>

            <p class="mb-5">
              Park Rangers assist in controlling a State or national park, scenic area, historic
              site, nature reserve, recreation area, or conservation reserve in accordance with
              authorised policies and priorities.
            </p>
            <h4 class="mb-5">What education they need:</h4>
            <p class="mb-5">
              You can work as a Park Ranger without formal qualifications, however, a qualification
              in land or park management or a similar field is often required. Vocational Education
              and Training (VET) and university are both common study pathways.
            </p>
            <p class="mb-5">
              Before starting a course, check it will provide you with the skills and qualifications
              you need. Visit:
            </p>

            <p class="mb-5">
              <a href="https://www.courseseeker.edu.au/" target="_blank">Course Seeker</a> is a
              useful resource to search and compare higher education courses.
            </p>
            <p class="mb-5">
              <a href="https://www.compared.edu.au/" target="_blank">ComparED</a> to compare
              undergraduate and postgraduate student experiences and outcomes.
            </p>
            <p class="mb-5">
              <a href="https://www.myskills.gov.au/" target="_blank">My Skills</a> to compare
              Vocational Education and Training (VET) courses, providers, and student outcomes.
            </p>
            <p class="mb-10">
              <a href="https://www.aapathways.com.au/career-research/job-pathways" target="_blank"
                >AAPathways</a
              >
              website to explore Agriculture, Horticulture and Conservation & Land Management and
              Sustainability VET training pathways.
            </p>
          </vx-card>
        </div>
      </vs-tab>
      <vs-tab label="Conservation Officer">
        <div class="py-2">
          <vx-card class="no-shadow">
            <h4 class="mb-5">What they do:</h4>

            <p class="mb-5">
              Conservation Officers develop and implement programs and regulations for the
              protection of fish, wildlife and other natural resources.
            </p>
            <p class="mb-5">Specialisations: Landcare Facilitator.</p>
            <h4 class="mb-5">What education they need:</h4>
            <p class="mb-5">
              You usually need a bachelor’s degree in a relevant field to work as a Conservation
              Officer. Some workers have a Vocational Education and Training (VET) qualification.
            </p>
            <p class="mb-5">
              Before starting a course, check it will provide you with the skills and qualifications
              you need. Visit:
            </p>
            <p class="mb-5">
              <a href="https://www.courseseeker.edu.au/" target="_blank">Course Seeker</a> is a
              useful resource to search and compare higher education courses.
            </p>
            <p class="mb-5">
              <a href="https://www.compared.edu.au/" target="_blank">ComparED</a> to compare
              undergraduate and postgraduate student experiences and outcomes.
            </p>
            <p class="mb-5">
              <a href="https://www.myskills.gov.au/" target="_blank">My Skills</a> to compare
              Vocational Education and Training (VET) courses, providers, and student outcomes.
            </p>
            <p class="mb-10">
              <a href="https://www.aapathways.com.au/career-research/job-pathways" target="_blank"
                >AAPathways</a
              >
              website to explore Agriculture, Horticulture and Conservation & Land Management and
              Sustainability VET training pathways.
            </p>
          </vx-card>
        </div>
      </vs-tab>
      <vs-tab label="Environmental Scientist">
        <div class="py-2">
          <vx-card class="no-shadow">
            <h4 class="mb-5">What they do:</h4>
            <p class="mb-5">
              Environmental Scientists study, develop, implement and advise on policies and plans
              for managing and protecting the environment, flora, fauna and other natural resources.
            </p>
            <h4 class="mb-5">What education they need:</h4>
            <p class="mb-5">
              You usually need a bachelor’s degree in a relevant field to work as an Environmental
              Scientist. It is also common to complete postgraduate studies.
            </p>
            <p class="mb-5">
              Before starting a course, check it will provide you with the skills and qualifications
              you need. Visit:
            </p>

            <p class="mb-5">
              <a href="https://www.courseseeker.edu.au/" target="_blank">Course Seeker</a> is a
              useful resource to search and compare higher education courses.
            </p>
            <p class="mb-5">
              <a href="https://www.compared.edu.au/" target="_blank">ComparED</a> to compare
              undergraduate and postgraduate student experiences and outcomes.
            </p>
            <p class="mb-5">
              <a href="https://www.myskills.gov.au/" target="_blank">My Skills</a> to compare
              Vocational Education and Training (VET) courses, providers, and student outcomes.
            </p>
          </vx-card>
        </div>
      </vs-tab>
    </vs-tabs>

    <h4 class="mb-5">Quick Details:</h4>

    <vs-tabs alignment="fixed" v-model="activeTab" class="mb-5 no-padding overwrite-tabs">
      <vs-tab label="Park Ranger">
        <div class="py-2">
          <vx-card class="no-shadow">
            <div class="vx-row flex justify-center">
              <div class="vx-col xl:w-2/3 lg:w-1/2 w-full mb-5">
                <h4 class="mb-5">Park Ranger</h4>
                <ul class="default-list">
                  <li><strong>Size:</strong> This is a very small occupation.</li>
                  <li>
                    <strong>Full-time:</strong> Most work full-time (81%, much higher than the
                    average of 66%).
                  </li>
                  <li>
                    <strong>Hours:</strong> Full-time workers spend around 41 hours per week at work
                    (compared to an average of 44 hours).
                  </li>
                  <li>
                    <strong>Age:</strong> The average age is 42 years (compared to the average of 40
                    years).
                  </li>
                  <li>
                    <strong>Gender:</strong> 25% of workers are female (compared to the average of
                    48%).
                  </li>
                </ul>
                <vs-alert color="dark" title="Caution" active="true" class="mb-5">
                  The Australian jobs market is changing in response to the COVID-19 pandemic. These
                  estimates do not take account of the impact of COVID-19. They may not reflect the
                  current jobs market and should be used and interpreted with extreme caution.
                </vs-alert>

                <h4 class="mb-5">Tasks</h4>
                <ul class="default-list">
                  <li>
                    Implements policies and organises activities in designated parks and other areas
                    to conserve and protect natural and cultural heritage.
                  </li>
                  <li>
                    Participates in management planning by providing environmental information and
                    making inventories of plants, animals and items of cultural and heritage
                    significance.
                  </li>
                </ul>

                <h4 class="mb-5">Useful Resources:</h4>
                <p class="mb-5">
                  <a href="https://www.parksleisure.com.au/" target="_blank"
                    >Parks & Leisure Australia</a
                  >
                </p>
                <p class="mb-5">
                  <a href="https://www.nswranger.org.au/" target="_blank"
                    >Australian Institute of Local Government Rangers</a
                  >
                </p>
                <p class="mb-5">
                  <a href="https://warangers.asn.au/" target="_blank">WA Rangers Association</a>
                </p>
                <p class="mb-5">
                  <a href="https://arcv.org.au/" target="_blank"
                    >Association of Rangers and Conservationists Victoria</a
                  >
                </p>
                <p class="mb-5">
                  <a href="https://www.sarangerassociation.org/" target="_blank"
                    >SA Ranger Association</a
                  >
                </p>
                <p class="mb-5">
                  <a href="https://queenslandranger.org/" target="_blank"
                    >Queensland Ranger Association</a
                  >
                </p>
                <p class="mb-5">
                  <a href="https://parks.tas.gov.au/" target="_blank"
                    >Tasmania Parks and Wildlife Service</a
                  >
                </p>
              </div>
              <div class="vx-col xl:w-1/3 lg:w-1/2 w-full my-5">
                <img
                  src="@/assets/images/experiences/envscience/jo1.png"
                  class="img-responsive"
                  alt="Environmental Scientists job outlook: $1,779 weekly pay, strong future growth. Park Rangers: 2,500 workers, very high skill level, 81% full-time, 41 hours average full-time week, average age 42, 25% female."
                />
                <p class="mt-5">
                  Source: <a href="https://joboutlook.gov.au/" target="_blank">Job Outlook</a>
                </p>
              </div>
            </div>
          </vx-card>
        </div>
      </vs-tab>
      <vs-tab label="Conservation Officer">
        <div class="py-2">
          <vx-card class="no-shadow">
            <div class="vx-row flex justify-center">
              <div class="vx-col xl:w-2/3 lg:w-1/2 w-full mb-5">
                <h4 class="mb-5">Conservation Officer</h4>
                <ul class="default-list">
                  <li><strong>Size:</strong> This is a very small occupation.</li>
                  <li>
                    <strong>Full-time:</strong> Many work full-time (76%, higher than the average of
                    66%).
                  </li>
                  <li>
                    <strong>Hours:</strong> Full-time workers spend around 41 hours per week at work
                    (compared to an average of 44 hours).
                  </li>
                  <li>
                    <strong>Age:</strong> The average age is 39 years (compared to the average of 40
                    years).
                  </li>
                  <li>
                    <strong>Gender:</strong> 47% of workers are female (compared to the average of
                    48%).
                  </li>
                </ul>
                <vs-alert color="dark" title="Caution" active="true" class="mb-5">
                  The Australian jobs market is changing in response to the COVID-19 pandemic. These
                  estimates do not take account of the impact of COVID-19. They may not reflect the
                  current jobs market and should be used and interpreted with extreme caution.
                </vs-alert>

                <h4 class="mb-5">Tasks</h4>
                <ul class="default-list">
                  <li>
                    Evaluates habitat, wildlife and fisheries needs, and formulates short and
                    long-term management goals and objectives.
                  </li>
                  <li>Enforces laws and regulations to conserve and protect fish and wildlife.</li>
                </ul>

                <h4 class="mb-5">Useful Resources:</h4>
                <p class="mb-5">
                  <a href="http://www.nrm.gov.au/" target="_blank">National Landcare Program</a>
                </p>
              </div>
              <div class="vx-col xl:w-1/3 lg:w-1/2 w-full my-5">
                <img
                  src="@/assets/images/experiences/envscience/jo2.png"
                  alt="Environmental Scientists job outlook: $1,779 weekly pay, strong future growth. Conservation Officers: 4,200 workers, very high skill level, 76% full-time, 41 hours average full-time week, average age 39, 47% female."
                  class="img-responsive"
                />
                <p class="mt-5">
                  Source: <a href="https://joboutlook.gov.au/" target="_blank">Job Outlook</a>
                </p>
              </div>
            </div>
          </vx-card>
        </div>
      </vs-tab>
      <vs-tab label="Environmental Scientist">
        <div class="py-2">
          <vx-card class="no-shadow">
            <div class="vx-row mt-5 flex justify-center">
              <div class="vx-col xl:w-2/3 lg:w-1/2 w-full my-5">
                <h4 class="mb-5">Environmental Scientist</h4>
                <ul class="default-list">
                  <li><strong>Size:</strong> This is a large occupation.</li>
                  <li>
                    <strong>Earnings:</strong> Full-time workers on an adult wage earn around $1,779
                    per week (higher than the average of $1,460). Earnings tend to be lower when
                    starting out and higher as experience grows.
                  </li>
                  <li>
                    <strong>Full-time:</strong> Many work full-time (76%, higher than the average of
                    66%).
                  </li>
                  <li>
                    <strong>Hours:</strong> Full-time workers spend around 43 hours per week at work
                    (compared to an average of 44 hours).
                  </li>
                  <li>
                    <strong>Age:</strong> The average age is 39 years (compared to the average of 40
                    years).
                  </li>
                  <li>
                    <strong>Gender:</strong> 41% of workers are female (compared to the average of
                    48%).
                  </li>
                </ul>
                <vs-alert color="dark" title="Caution" active="true" class="mb-5">
                  The Australian jobs market is changing in response to the COVID-19 pandemic. These
                  estimates do not take account of the impact of COVID-19. They may not reflect the
                  current jobs market and should be used and interpreted with extreme caution.
                </vs-alert>

                <h4 class="mb-5">Tasks</h4>
                <ul class="default-list">
                  <li>
                    Evaluating habitat, wildlife and fisheries needs, and formulating short and
                    long-term management goals and objectives
                  </li>
                  <li>Enforcing laws and regulations to conserve and protect fish and wildlife</li>
                  <li>
                    Carrying out environmental impact assessments for a wide range of development
                    projects
                  </li>
                  <li>Proposing solutions to address negative environmental impact</li>
                  <li>
                    Studying the effects of factors, such as terrain, altitude, climatic and
                    environmental change, sources of nutrition, predators and the impacts of humans,
                    on animal and plant life
                  </li>
                  <li>
                    Studying and analysing pollution, atmospheric conditions, demographic
                    characteristics, ecology, mineral, soil and water samples
                  </li>
                  <li>
                    Developing conservation and management policies for biological resources, such
                    as fish populations and forests, and establishing standards and
                  </li>
                  <li>
                    Developing approaches for the control of pollution and the rehabilitation of
                    areas disturbed by activities such as mining, timber felling and overgrazing
                  </li>
                  <li>
                    Implementing policies and organising activities in designated parks and other
                    areas to conserve and protect natural and cultural heritage
                  </li>
                  <li>
                    Participating in management planning by providing environmental information and
                    making inventories of plants, animals and items of cultural and heritage
                    significance
                  </li>
                </ul>

                <h4 class="mb-5">Useful Resources:</h4>
                <p class="mb-5">
                  <a href="https://www.eianz.org/" target="_blank"
                    >Environment Institute of Australia and New Zealand</a
                  >
                </p>
                <p class="mb-5">
                  <a href="http://www.nrm.gov.au/" target="_blank">National Landcare Program</a>
                </p>
                <p class="mb-5">
                  <a href="https://www.csiro.au/" target="_blank"
                    >Commonwealth Scientific and Industrial Research Organisation (CSIRO)</a
                  >
                </p>
                <p class="mb-5">
                  <a href="https://www.parksleisure.com.au/" target="_blank"
                    >Parks & Leisure Australia</a
                  >
                </p>
                <p class="mb-5">
                  <a href="https://www.nswranger.org.au/" target="_blank"
                    >Australian Institute of Local Government Rangers</a
                  >
                </p>
                <p class="mb-5">
                  <a href="https://warangers.asn.au/" target="_blank">WA Rangers Association</a>
                </p>
                <p class="mb-5">
                  <a href="https://arcv.org.au/" target="_blank"
                    >Association of Rangers and Conservationists Victoria</a
                  >
                </p>
                <p class="mb-5">
                  <a href="https://www.sarangerassociation.org/" target="_blank"
                    >SA Ranger Association</a
                  >
                </p>
                <p class="mb-5">
                  <a href="https://queenslandranger.org/" target="_blank"
                    >Queensland Ranger Association</a
                  >
                </p>
              </div>
              <div class="vx-col xl:w-1/3 lg:w-1/2 w-full my-5">
                <img
                  src="@/assets/images/experiences/envscience/jo3.png"
                  alt="Environmental Scientists job outlook: $1,779 weekly pay, strong future growth, 21,100 workers, very high skill level, 76% full-time, 43 hours average full-time week, average age 39, 41% female."
                  class="img-responsive"
                />
                <p class="mt-5">
                  Source: <a href="https://joboutlook.gov.au/" target="_blank">Job Outlook</a>
                </p>
              </div>
            </div>
          </vx-card>
        </div>
      </vs-tab>
    </vs-tabs>
    <vx-card title="Interesting Fact">
      <div class="vx-row flex justify-center">
        <div class="vx-col xl:w-2/3 lg:w-1/2 w-full mb-5">
          <p class="mb-5">
            Did you know that a <strong>Sperm whale’s poop</strong> offsets carbon in the
            environment? A whale’s poop is very beneficial to the environment as it offsets carbon
            in the atmosphere. Each whale’s poop releases about 50 tonnes of iron annually that
            promote the growth of phytoplankton, which take in carbon dioxide.
          </p>
        </div>
        <div class="vx-col xl:w-1/3 lg:w-1/2 w-full mb-5">
          <img
            src="@/assets/images/experiences/envscience/new_whale_image.jpg"
            alt=""
            class="img-responsive"
          />
        </div>
      </div>
    </vx-card>
  </div>
</template>

<script>
export default {
  data() {
    return {
      cover_img: require('@/assets/images/other/cover.jpg'),
      popupActive: false,
      activeTab: 0,
    };
  },
};
</script>
